import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';

import ElkoninBox from './ElkoninBox';
import CharBank from './CharBank';
import {throttle} from '../util';

import banners from '../data/whiteboards';
import clipart from '../data/clipart';
import {CHAR_BANK_ID, CHAR_BANK_2_ID} from '../constants';

import trashcanImg from '../images/trashcan.svg';

class Designer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      windowDimensions: this.windowDimensions(),
      isDragging: false,
      beingDragged: null,
    };

    throttle('resize', 'throttleResize');
    this.handleWindowResize = this.handleWindowResize.bind(this);
  }

  componentDidMount() {
    this.handleWindowResize();
    window.addEventListener('throttleResize', this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('throttleResize', this.handleWindowResize);
  }

  windowDimensions() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    return {width, height};
  }

  handleWindowResize() {
    const windowDimensions = this.windowDimensions();
    const charWidth = this.calculateCharWidth(windowDimensions);

    this.setState({windowDimensions, charWidth});

    this.props.handleCardContainerResize();
  }

  // Char
  calculateCharWidth(windowDimensions) {
    // if (windowDimensions.width >= 1440) {
    //   // Extra Extra large
    //   return 115;
    // } else if (windowDimensions.width >= 1200) {
    //   // Extra large
    //   return 90;
    // } else if (windowDimensions.width >= 992) {
    //   // Large
    //   return 75;
    // } else if (windowDimensions.width >= 768) {
    //   // Medium
    //   return 60;
    // }
    // return 60;

    if (windowDimensions.width >= 1800) {
      // Extra Extra large
      return 115;
    } else if (windowDimensions.width >= 992) {
      // Large
      return 90;
    }
    return 60;
  }

  // Drag handlers
  onBeforeCapture = () => {
    this.setState({isDragging: true});
  };

  onDragStart = (start) => {
    this.setState({
      beingDragged: start,
    });
  };

  onDragEnd = (result) => {
    this.setState({isDragging: false, beingDragged: null});
    this.props.handleDragEnd(result);
  };

  render() {
    const {boxes, boxOrder, banner, clipartID, switchCharBank} = this.props;
    const bannerModel = banners[banner];
    const clipartModel = clipart[clipartID];
    const hasElkoninBox = Object.values(boxes).some((box) => box.type === 'BOX');
    const HEADER_HEIGHT = 96;
    const VERTICAL_PADDING = 64; // 32 top, 32 bottom

    // Calculate size of banner while keeping aspect ratio
    let bannerWidth = this.state.windowDimensions.width;
    let bannerHeight = this.state.windowDimensions.height - HEADER_HEIGHT;
    if (banner) {
      const maxBannerHeight = this.state.windowDimensions.height - HEADER_HEIGHT - VERTICAL_PADDING;
      const bannerRatio = bannerModel.pixelWidth / bannerModel.pixelHeight;

      bannerHeight = bannerWidth / bannerRatio;

      if (bannerHeight >= maxBannerHeight) {
        bannerHeight = maxBannerHeight;
        bannerWidth = bannerHeight * bannerRatio;
      }
    }

    const charBankHeight = Math.round(0.2 * bannerHeight);

    return (
      <div className="designer">
        <div className="designer-background" />

        {banner && (
          <div
            className="designer-banner-container"
            style={{
              width: bannerWidth,
              height: bannerHeight,
              backgroundImage: `url(${bannerModel.imageSrc})`,
            }}
          >
            <DragDropContext
              onBeforeCapture={this.onBeforeCapture}
              onDragStart={this.onDragStart}
              onDragEnd={this.onDragEnd}
            >
              {this.state.isDragging && (
                <Droppable droppableId="trashcan">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`designer-trashcan${snapshot.isDraggingOver ? ' drag-hover' : ''}`}
                      style={{height: charBankHeight, width: charBankHeight}}
                    >
                      <img src={trashcanImg} alt="trash" />
                    </div>
                  )}
                </Droppable>
              )}

              <div className="designer-banner-body">
                {clipartModel && (
                  <Droppable droppableId="clipart" isDropDisabled={true}>
                    {(dropProvided) => (
                      <div {...dropProvided.droppableProps} ref={dropProvided.innerRef}>
                        <Draggable draggableId={clipartModel.id} index={0}>
                          {(provided) => (
                            <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                              <img src={clipartModel.imageSrc} className="designer-clipart" alt="clipart" />
                            </div>
                          )}
                        </Draggable>
                        {dropProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}

                {!hasElkoninBox ? (
                  <CharBank
                    boxes={boxes}
                    box={boxes[CHAR_BANK_2_ID]}
                    charWidth={this.state.charWidth}
                    beingDragged={this.state.beingDragged}
                    height={charBankHeight}
                    hasElkoninBox={hasElkoninBox}
                    switchCharBank={switchCharBank}
                  />
                ) : (
                  <Droppable
                    droppableId="all-elkonin-boxes"
                    direction="horizontal"
                    isDropDisabled={this.state.beingDragged?.source?.droppableId !== 'all-elkonin-boxes'}
                  >
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="designer-elkonin-boxes">
                        {boxOrder.map((id, index) => (
                          <ElkoninBox
                            key={id}
                            index={index}
                            box={boxes[id]}
                            beingDragged={this.state.beingDragged}
                            charWidth={this.state.charWidth}
                            boxes={boxes}
                            height={charBankHeight}
                          />
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </div>

              <CharBank
                boxes={boxes}
                box={boxes[CHAR_BANK_ID]}
                charWidth={this.state.charWidth}
                beingDragged={this.state.beingDragged}
                height={charBankHeight}
                hasElkoninBox={hasElkoninBox}
                switchCharBank={switchCharBank}
              />
            </DragDropContext>
          </div>
        )}
      </div>
    );
  }
}

Designer.propTypes = {
  boxes: PropTypes.object.isRequired,
  banner: PropTypes.string,
};

export default Designer;
